import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  Grid,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Translate as TranslateIcon,
  Brush as BrushIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";
import ControlsBar from "./ControlsBar";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "./SEOHead";

const Feature: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
      <Box
        sx={{
          color: "primary.main",
          mt: 0.3,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const About: React.FC = () => {
  const { isRTL } = useLanguage();
  return (
    <>
      <SEOHead
        title="About | Resume Optimizer AI — Free AI Resume Tool for UAE Job Seekers"
        description="Learn about Resume Optimizer AI — a free tool that helps job seekers in the UAE create ATS-friendly, professionally optimized resumes using AI in seconds."
        keywords="about resume optimizer, free AI resume tool UAE, ATS resume builder, resume AI Dubai"
        canonicalUrl="https://my-aitools.online/resume-optimizer/#/about"
      />
      <ControlsBar />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 6,
        }}
      >
        <Container maxWidth="md">
          {/* Back link */}
          <Link
            href="#/"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 4,
              color: "primary.main",
              fontWeight: 600,
              fontSize: "0.95rem",
            }}
          >
            {isRTL ? "→" : "←"} Back to Resume Optimizer
          </Link>

          <Paper
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              backgroundColor: "background.paper",
            }}
          >
            {/* Header */}
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 1 }}
            >
              About my-aitools.online
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
              Free AI-powered tools built to help job seekers put their best foot forward.
            </Typography>

            <Divider sx={{ mb: 4 }} />

            {/* What is it */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}>
                What is the Resume Optimizer?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                The Resume Optimizer is a free AI tool that rewrites and tailors your
                resume to match a specific job description. Instead of sending the same
                generic resume to every employer, you get a version that speaks directly
                to what the hiring manager is looking for — improving your chances of
                passing ATS (Applicant Tracking System) filters and landing interviews.
              </Typography>
            </Box>

            {/* How it works */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}>
                How does it work?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                You upload or paste your existing resume, then paste the job description
                you're targeting. Our AI analyzes both and rewrites your resume to
                highlight the most relevant skills and experiences — without fabricating
                anything. You can then edit the result, reorder sections, and download
                a professionally formatted PDF in multiple design templates.
              </Typography>
            </Box>

            {/* Features */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: "primary.main" }}>
                Features
              </Typography>
              <Grid container spacing={3} columns={12}>
                <Feature
                  icon={<BoltIcon />}
                  title="AI-Powered Optimization"
                  description="Uses state-of-the-art AI models to rewrite your resume for maximum relevance to any job description."
                />
                <Feature
                  icon={<DownloadIcon />}
                  title="Multiple PDF Templates"
                  description="Download your optimized resume in 5 professionally designed templates — Classic, Slate, Compact, Executive, and Blueprint."
                />
                <Feature
                  icon={<BrushIcon />}
                  title="Fully Editable"
                  description="Every section of the AI output is editable. Add, remove, or reorder experience, education, and skills with drag and drop."
                />
                <Feature
                  icon={<TranslateIcon />}
                  title="English & Arabic"
                  description="Full support for both English and Arabic, including right-to-left layout for Arabic users."
                />
                <Feature
                  icon={<SecurityIcon />}
                  title="Privacy First"
                  description="We never store your resume or personal data. Everything is processed in real time and immediately discarded."
                />
                <Feature
                  icon={<AutoAwesomeIcon />}
                  title="Completely Free"
                  description="No sign-up, no subscription, no hidden fees. Just upload, optimize, and download."
                />
              </Grid>
            </Box>

            {/* Who is it for */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}>
                Who is it for?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Anyone actively job hunting — whether you're a fresh graduate applying
                for your first role, a professional switching industries, or an
                experienced candidate targeting a specific position. The tool is
                especially useful in competitive markets like the UAE, where tailoring
                your resume to each application can make a significant difference.
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Footer note */}
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Questions or feedback? Reach us at{" "}
              <Link href="mailto:management@my-aitools.online" color="primary">
                management@my-aitools.online
              </Link>
              . Read our{" "}
              <Link href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" color="primary">
                Privacy Policy
              </Link>{" "}
              to understand how we handle your data.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default About;
